//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { ReactComponent as CircleStack } from 'assets/circle-stack.svg';
import { ReactComponent as ControllerIcon } from 'assets/controller-icon.svg';
import { ReactComponent as EapCircleIcon } from 'assets/eap-circle.svg';
import remove from 'assets/remove.svg';
import { ReactComponent as SwitchIcon } from 'assets/switch-icon.svg';
import Breadcrumbs from 'components/Breadcrumbs';
import DiagnosticDialog from 'components/DiagnosticDialog';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useModal } from 'contexts/ModalProvider';
import { DeviceType } from 'dto/response-bin-info.dto';
import { OptionType } from 'models/option-type';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BuildVersionList } from 'utils/helper';

interface CardItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  actions: { label: string; onClick: () => void }[];
  bgColor: string;
}

export enum DiagnosticType {
  SYSTEM = 'system',
  GATEWAY = 'gateway',
  EAP = 'eap',
  SWITCH = 'switch',
}

const Diagnostic = () => {
  const { t } = useTranslation();
  const { buildVersion } = useConfig();
  const { getOnboardedDevices } = useAuth();
  const { addCustomModal } = useModal();
  const [gatewayDeviceOptions, setGatewayDeviceOptions] = useState<OptionType[]>([]);
  const [eapDeviceOptions, setEapDeviceOptions] = useState<OptionType[]>([]);
  const [switchDeviceOptions, setSwitchDeviceOptions] = useState<OptionType[]>([]);
  const diagnosticeDialogRef = useRef<{ onCloseDiagnosticDialog: () => void }>(null);

  const cards: CardItem[] = [
    {
      id: DiagnosticType.SYSTEM,
      title: t('diagnostic.system.title'),
      description: t('diagnostic.system.description'),
      icon: <CircleStack stroke="#fff" />,
      actions: [{ label: t('diagnostic.diagnoseBtn'), onClick: () => onClickDiagnostic(DiagnosticType.SYSTEM) }],
      bgColor: '#8884d8',
    },
    ...(buildVersion !== BuildVersionList.portal
      ? [
          {
            id: DiagnosticType.GATEWAY,
            title: t('diagnostic.gateway.title'),
            description: t('diagnostic.gateway.description'),
            icon: <ControllerIcon fill="#fff" />,
            actions: [
              { label: t('diagnostic.diagnoseBtn'), onClick: () => onClickDiagnostic(DiagnosticType.GATEWAY) },
              // TODO: 後續再實作
              // { label: 'Ping', onClick: () => console.log('Ping') },
            ],
            bgColor: '#e0b1cb',
          },
        ]
      : []),
    {
      id: DiagnosticType.SWITCH,
      title: t('diagnostic.switch.title'),
      description: t('diagnostic.switch.description'),
      icon: <SwitchIcon fill="#fff" />,
      actions: [
        { label: t('diagnostic.diagnoseBtn'), onClick: () => onClickDiagnostic(DiagnosticType.SWITCH) },
        // TODO: 後續再實作
        // { label: 'Ping', onClick: () => console.log('Ping') },
        // { label: 'Traceroute', onClick: () => console.log('Traceroute') },
        // { label: 'Nslookup', onClick: () => console.log('Nslookup') },
        // { label: 'Locate', onClick: () => console.log('Locate') },
      ],
      bgColor: '#91D6E3',
    },
    {
      id: DiagnosticType.EAP,
      title: t('diagnostic.eap.title'),
      description: t('diagnostic.eap.description'),
      icon: <EapCircleIcon className="w-12 h-12" fill="#fff" />,
      actions: [
        { label: t('diagnostic.diagnoseBtn'), onClick: () => onClickDiagnostic(DiagnosticType.EAP) },
        // { label: 'Locate', onClick: () => console.log('Locate') },
      ],
      bgColor: '#00ADEA',
    },
  ];

  const onClickDiagnostic = (diagnosticType: DiagnosticType) => {
    const deviceTitle = cards.find((c) => c.id === diagnosticType)?.title ?? '';
    let devicesOptions: OptionType[];
    switch (diagnosticType) {
      case DiagnosticType.GATEWAY:
        devicesOptions = gatewayDeviceOptions;
        break;
      case DiagnosticType.EAP:
        devicesOptions = eapDeviceOptions;
        break;
      case DiagnosticType.SWITCH:
        devicesOptions = switchDeviceOptions;
        break;
      default:
        devicesOptions = [];
        break;
    }

    addCustomModal({
      open: true,
      name: 'diagnosticDialog',
      content: (
        <div className="flex flex-col">
          <div className="flex justify-between">
            <h2 className="card-title">{t('diagnostic.modalTitle', { type: deviceTitle })}</h2>
            <button type="button"
              className="btn btn-ghost self-end hover:bg-transparent"
              onClick={() => diagnosticeDialogRef.current?.onCloseDiagnosticDialog()}
              data-testid={`diagnostic-close-btn-for-type-${diagnosticType}`}
            >
              <img src={remove} className="w-4 h-4 dark:svg-dark-mode" alt="close" />
            </button>
          </div>
          <DiagnosticDialog ref={diagnosticeDialogRef} diagnosticType={diagnosticType} devicesOptions={devicesOptions} />
        </div>
      ),
      width: 700,
    });
  };

  const getDevicesOptions = async () => {
    getOnboardedDevices().then((response) => {
      const { data } = response;
      const eapOptions: OptionType[] = [];
      const switchOptions: OptionType[] = [];
      data.forEach((complexDevice) => {
        if (complexDevice.deviceType === DeviceType.Controller && complexDevice.wsConnected) {
          setGatewayDeviceOptions([{ value: complexDevice.serialNumber, label: `${complexDevice.nickname}(${complexDevice.macAddress})` }]);
        }
        if (complexDevice.deviceType === DeviceType.Eap && complexDevice.wsConnected) {
          eapOptions.push({ value: complexDevice.serialNumber, label: `${complexDevice.nickname}(${complexDevice.macAddress})` });
        }
        if (complexDevice.deviceType === DeviceType.Switch && complexDevice.wsConnected) {
          switchOptions.push({ value: complexDevice.serialNumber, label: `${complexDevice.nickname}(${complexDevice.macAddress})` });
        }
      });
      if (eapOptions.length > 0) {
        setEapDeviceOptions(eapOptions);
      }
      if (switchOptions.length > 0) {
        setSwitchDeviceOptions(switchOptions);
      }
    });
  };

  useEffect(() => {
    getDevicesOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full flex-1 relative flex h-full flex-col flex-nowrap opacity-100 overflow-auto">
      <div className="float-left w-full shadow leading-9 pl-4">
        <Breadcrumbs />
      </div>
      <div className="h-full sm:h-screen p-4 dark:bg-gray-500">
        <div className="grid grid-cols-3 gap-3">
          {cards.map((card) => (
            <div
              key={card.id}
              className="card card-border card-sm h-36 bg-base-100 rounded-lg drop-shadow-md"
              data-testid={`diagnostic-tool-for-name-${card.id}`}
            >
              <div className="card-body flex flex-row space-x-3">
                <div className="w-12 h-12 p-2 rounded-sm flex items-center justify-center" style={{ backgroundColor: card.bgColor }}>
                  {card.icon}
                </div>
                <div>
                  <h2 className="font-semibold text-lg">{card.title}</h2>
                  <p className="text-sm">{card.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {card.actions.map((a) => (
                      <button type="button" key={a.label} className="btn-link" onClick={a.onClick}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Diagnostic;
